import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import './admin.css';

export default function AdminPostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`admin/posts/${postId}/`)
      .then((r) => setPost(r.data))
      .catch((e) => toast.error(e.response?.data?.error || 'Failed to load post'))
      .finally(() => setLoading(false));
  }, [postId]);

  const remove = async () => {
    if (!window.confirm('Delete this post and its comments/likes?')) return;
    try {
      await api.delete(`admin/posts/${postId}/`);
      toast.success('Post deleted');
      navigate('/admin/posts', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Delete failed');
    }
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  }
  if (!post) {
    return (
      <div>
        <Link to="/admin/posts" className="btn btn-outline-secondary rounded-pill mb-3">← Back</Link>
        <div className="alert alert-warning">Post not found</div>
      </div>
    );
  }

  const title = post.title || 'Untitled post';

  return (
    <div className="admin-post-detail">
      <div className="admin-post-detail-top">
        <Link to="/admin/posts" className="btn btn-outline-secondary rounded-pill px-3">
          ← Back to posts
        </Link>
        <button type="button" className="btn btn-outline-danger rounded-pill px-3" onClick={remove}>
          Delete post
        </button>
      </div>

      <div className={`admin-card admin-post-hero ${post.image ? '' : 'no-media'}`}>
        {post.image ? (
          <div className="admin-post-hero-media">
            <img src={post.image} alt={title} />
          </div>
        ) : null}

        <div className="admin-post-hero-body">
          <h1 className="admin-post-hero-title">{title}</h1>

          <div className="admin-post-hero-meta">
            <div>
              <div className="fw-semibold">{post.doctor_name || '—'}</div>
              {post.doctor_email ? (
                <div className="small text-muted">{post.doctor_email}</div>
              ) : null}
            </div>
            <div className="admin-post-hero-dates small text-muted">
              <div>
                Created:{' '}
                <span className="text-dark fw-semibold">
                  {post.created_at ? new Date(post.created_at).toLocaleString() : '—'}
                </span>
              </div>
              <div>
                Updated:{' '}
                <span className="text-dark fw-semibold">
                  {post.updated_at ? new Date(post.updated_at).toLocaleString() : '—'}
                </span>
              </div>
            </div>
          </div>

          <div className="admin-post-stats">
            <div className="admin-post-stat">
              <i className="bi bi-heart-fill" />
              <strong>{post.likes_count ?? 0}</strong>
              <span>Likes</span>
            </div>
            <div className="admin-post-stat">
              <i className="bi bi-chat-dots-fill" />
              <strong>{post.comments_count ?? 0}</strong>
              <span>Comments</span>
            </div>
            <div className="admin-post-stat">
              <i className="bi bi-bookmark-fill" />
              <strong>{post.saves_count ?? 0}</strong>
              <span>Saves</span>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-card p-4 mb-3">
        <h6 className="fw-bold mb-3">Post content</h6>
        {post.short_content ? (
          <div className="mb-3">
            <div className="small text-muted mb-1">Summary</div>
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{post.short_content}</p>
          </div>
        ) : null}
        {post.large_content ? (
          <div>
            <div className="small text-muted mb-1">Full content</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{post.large_content}</div>
          </div>
        ) : (
          !post.short_content && <p className="text-muted mb-0">No content</p>
        )}
      </div>

      <div className="admin-card p-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold mb-0">Comments</h6>
          <span className="admin-badge default">
            {post.comments_count ?? (post.comments || []).length}
          </span>
        </div>
        {(post.comments || []).length === 0 ? (
          <p className="text-muted small mb-0">No comments yet.</p>
        ) : (
          <div className="admin-post-comments">
            {(post.comments || []).map((c) => (
              <div key={c.id} className="admin-post-comment">
                <div className="d-flex justify-content-between gap-2 mb-1">
                  <span className="fw-semibold small">
                    {c.author}
                    {c.author_type ? (
                      <span className="text-muted fw-normal"> · {c.author_type}</span>
                    ) : null}
                  </span>
                  <span className="text-muted small text-nowrap">
                    {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                  </span>
                </div>
                <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{c.content}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="small text-muted mt-3 mb-0">Post ID: {post.id}</p>
    </div>
  );
}
